import { motion } from 'framer-motion';
import { Check, Circle } from 'lucide-react';
import { useMotionPresets } from './motion';

export const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { id: 'case', label: 'Upper and lowercase letters', test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v) },
  { id: 'number', label: 'At least one number', test: (v) => /\d/.test(v) },
  { id: 'symbol', label: 'At least one symbol', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const LEVELS = [
  { label: 'Too short', bar: 'bg-slate-200', text: 'text-muted-foreground' },
  { label: 'Weak', bar: 'bg-destructive', text: 'text-destructive' },
  { label: 'Fair', bar: 'bg-warning', text: 'text-warning' },
  { label: 'Good', bar: 'bg-primary', text: 'text-primary' },
  { label: 'Strong', bar: 'bg-success', text: 'text-success' },
];

/**
 * Scores a password against PASSWORD_RULES.
 *
 * Exported so the form can gate submission on the same number the meter draws —
 * a meter that says "Weak" next to an enabled submit button teaches people to
 * ignore the meter.
 */
export function evaluatePassword(value = '') {
  const passed = PASSWORD_RULES.filter((r) => r.test(value));
  return {
    score: passed.length,
    passedIds: passed.map((r) => r.id),
    isAcceptable: passed.length >= 3 && value.length >= 8,
  };
}

export function PasswordStrength({ value = '', showRules = true }) {
  const { reduced } = useMotionPresets();
  const { score } = evaluatePassword(value);
  const level = LEVELS[value ? score : 0];

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-2">
        <span className="text-[11px] font-body font-semibold uppercase tracking-wider text-muted-foreground">
          Password strength
        </span>
        <span className={`text-xs font-body font-semibold ${level.text}`}>
          {value ? level.label : '—'}
        </span>
      </div>

      <div
        className="flex gap-1.5"
        role="progressbar"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={PASSWORD_RULES.length}
        aria-label="Password strength"
      >
        {PASSWORD_RULES.map((_, i) => (
          <span key={i} className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
            <motion.span
              className={`block h-full rounded-full ${i < score ? level.bar : 'bg-transparent'}`}
              initial={false}
              animate={{ scaleX: i < score ? 1 : 0 }}
              style={{ transformOrigin: 'left' }}
              transition={{ duration: reduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
            />
          </span>
        ))}
      </div>

      {showRules && (
        <ul className="mt-4 grid sm:grid-cols-2 gap-y-2 gap-x-4">
          {PASSWORD_RULES.map((rule) => {
            const met = rule.test(value);
            return (
              <li key={rule.id} className="flex items-center gap-2 text-xs font-body">
                {met ? (
                  <Check className="w-3.5 h-3.5 shrink-0 text-success" aria-hidden="true" />
                ) : (
                  <Circle className="w-3.5 h-3.5 shrink-0 text-slate-300" aria-hidden="true" />
                )}
                <span className={met ? 'text-secondary' : 'text-muted-foreground'}>{rule.label}</span>
                <span className="sr-only">{met ? '— met' : '— not met'}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
